import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  Table,
  Form,
  Button,
  Alert,
  Badge,
  Spinner,
} from 'react-bootstrap';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { AsyncTypeahead } from 'react-bootstrap-typeahead';

import PageWrapper from 'components/PageWrapper';
import CommonLoader from 'components/CommonLoader';
import Pager from 'components/Pager';
import styles from './index.module.css';

import QAApi from 'services/QAApi';
import { MembershipDocumentOverviewDto, MembershipOwnerDto } from 'services/QAApi/types';

import useIsMountedRef from 'utils/hooks/useIsMountedRef';
import useToaster from 'utils/hooks/useToaster';
import HttpErrorHelper from 'utils/HttpErrorHelper';

interface FilterState {
  ownerId: string | null;
  sentToQaBy: string | null;
  isSent: boolean | null;
  searchQuery: string;
}

const ITEMS_PER_PAGE = 10;

const Filinnlesninger = () => {
  const navigate = useNavigate();
  const isMountedRef = useIsMountedRef();
  const toaster = useToaster();
  const toasterRef = useRef(toaster);

  useEffect(() => {
    toasterRef.current = toaster;
  }, [toaster]);

  const [data, setData] = useState<MembershipDocumentOverviewDto[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isFilterLoading, setIsFilterLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [hasMoreData, setHasMoreData] = useState<boolean>(true);
  const [totalCount, setTotalCount] = useState<number | null>(null);

  const [filters, setFilters] = useState<FilterState>({
    ownerId: null,
    sentToQaBy: null,
    isSent: null,
    searchQuery: '',
  });

  // Typeahead states for membership owners
  const [membershipOwners, setMembershipOwners] = useState<MembershipOwnerDto[]>([]);
  const [isLoadingOwners, setIsLoadingOwners] = useState(false);
  const [selectedOwner, setSelectedOwner] = useState<MembershipOwnerDto[]>([]);

  const loadDataInternal = useCallback(
    async (page: number, isNewSearch: boolean) => {
      if (!isMountedRef.current) return;

      if (isNewSearch) {
        setIsFilterLoading(true);
      }
      setIsLoading(true);
      try {
        const skip = (page - 1) * ITEMS_PER_PAGE;
        const result = await QAApi.getMembershipDocuments(
          filters.ownerId || undefined,
          filters.sentToQaBy || undefined,
          filters.isSent !== null ? filters.isSent : undefined,
          skip,
          ITEMS_PER_PAGE,
        );

        if (isMountedRef.current) {
          setHasMoreData(result.length === ITEMS_PER_PAGE);
          setData(result);

          // If we got a short page, we can infer the total count.
          setTotalCount(result.length < ITEMS_PER_PAGE ? skip + result.length : null);
        }
      } catch (error) {
        if (isMountedRef.current) {
          console.error('Error loading membership documents:', error);
          setData([]);
          setHasMoreData(false);
          setTotalCount(null);
          toasterRef.current.error(
            'Feil ved lasting av medlemskapsdokumenter',
            HttpErrorHelper.formatError(error),
          );
        }
      } finally {
        if (isMountedRef.current) {
          setIsLoading(false);
          if (isNewSearch) {
            setIsFilterLoading(false);
          }
        }
      }
    },
    [filters, isMountedRef],
  );

  const handlePageChanged = useCallback(
    (page: number) => {
      if (page < 1 || isLoading || isFilterLoading) return;
      setCurrentPage(page);
      void loadDataInternal(page, false);
    },
    [isLoading, isFilterLoading, loadDataInternal],
  );

  useEffect(() => {
    // Any filter change resets paging.
    setCurrentPage(1);
    void loadDataInternal(1, true);
  }, [filters, loadDataInternal]);

  const handleFilterChange = (newFilters: Partial<FilterState>) => {
    setFilters((prev) => ({ ...prev, ...newFilters }));
  };

  const handleOwnerSearch = async (query: string) => {
    setIsLoadingOwners(true);
    try {
      const owners = await QAApi.getMembershipOwners(query || undefined);
      setMembershipOwners(owners);
    } catch (error) {
      console.error('Error loading membership owners:', error);
      setMembershipOwners([]);
    } finally {
      setIsLoadingOwners(false);
    }
  };

  const handleOwnerSelect = (selected: MembershipOwnerDto[]) => {
    setSelectedOwner(selected);
    const ownerId = selected.length > 0 ? selected[0].id : null;
    handleFilterChange({ ownerId });
  };

  const handleRowClick = (document: MembershipDocumentOverviewDto) => {
    navigate(`/qa/filinnlesninger/${document.id}`);
  };

  const formatDate = (date: Date | string | null | undefined) => {
    if (!date) return 'N/A';
    try {
      return format(new Date(date), 'dd.MM.yyyy HH:mm');
    } catch {
      return 'Ugyldig dato';
    }
  };

  const clearFilters = () => {
    setFilters({
      ownerId: null,
      sentToQaBy: null,
      isSent: null,
      searchQuery: '',
    });
    setSelectedOwner([]);
  };

  const skip = (currentPage - 1) * ITEMS_PER_PAGE;
  const fromItem = data.length > 0 ? skip + 1 : 0;
  const toItem = skip + data.length;
  const pagerTotalItems = Math.max(
    0,
    totalCount ??
      (hasMoreData
        ? (currentPage + 1) * ITEMS_PER_PAGE
        : (currentPage - 1) * ITEMS_PER_PAGE + data.length),
  );

  return (
    <PageWrapper isFluid header="Filinnlesninger">
      <Container fluid>
        {((isLoading && data.length === 0) || isFilterLoading) && <CommonLoader />}

        {/* Filters */}
        <Card className="mb-3">
          <Card.Header>
            <h5 className="mb-0">Filtre</h5>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6} lg={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Eier</Form.Label>
                  <AsyncTypeahead
                    id="membership-owner-typeahead"
                    isLoading={isLoadingOwners}
                    labelKey="name"
                    minLength={1}
                    onSearch={handleOwnerSearch}
                    onChange={handleOwnerSelect}
                    onInputChange={(text) => {
                      // Convert input to uppercase
                      return text.toUpperCase();
                    }}
                    options={membershipOwners}
                    placeholder="Søk etter eier..."
                    selected={selectedOwner}
                    renderMenuItemChildren={(option, props) => (
                      <div>
                        <div>{(option as MembershipOwnerDto).name}</div>
                      </div>
                    )}
                  />
                </Form.Group>
              </Col>
              <Col md={6} lg={3}>
                <Form.Group className="mb-3">
                  <Form.Label>Sendt til QA</Form.Label>
                  <Form.Select
                    value={filters.isSent === null ? '' : filters.isSent.toString()}
                    onChange={(e) => {
                      const value = e.target.value;
                      handleFilterChange({
                        isSent: value === '' ? null : value === 'true',
                      });
                    }}
                  >
                    <option value="">Alle</option>
                    <option value="true">Ja</option>
                    <option value="false">Nei</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={6} lg={3}>
                <Form.Group className="mb-3">
                  <Form.Label>&nbsp;</Form.Label>
                  <div>
                    <Button variant="outline-secondary" onClick={clearFilters}>
                      Tøm filtre
                    </Button>
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Results */}
        {data.length > 0 ? (
          <>
            <Alert variant="info" className="mb-3">
              <div className="d-flex align-items-center justify-content-between">
                <div>
                  {totalCount !== null
                    ? `Viser ${fromItem}-${toItem} av ${totalCount} dokumenter (side ${currentPage})`
                    : `Viser ${fromItem}-${toItem} dokumenter (side ${currentPage})`}
                </div>
                {(isLoading || isFilterLoading) && (
                  <div className="d-flex align-items-center">
                    <Spinner animation="border" size="sm" className="me-2" />
                    Laster...
                  </div>
                )}
              </div>
            </Alert>

            <div>
              <Table responsive hover>
                <thead>
                  <tr>
                    <th>Eier</th>
                    <th>Lesedato</th>
                    <th>Sendt til QA</th>
                    <th>Sendt av</th>
                    <th>Total medlemmer</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((doc) => (
                    <tr
                      key={doc.id}
                      onClick={() => handleRowClick(doc)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td>{doc.ownerName}</td>
                      <td>{formatDate(doc.readAt)}</td>
                      <td>
                        {doc.sentToQaAt ? (
                          <Badge bg="success">Ja</Badge>
                        ) : (
                          <Badge bg="secondary">Nei</Badge>
                        )}
                      </td>
                      <td>{doc.sentToQaBy}</td>
                      <td>{doc.totalMembers}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <div className="d-flex justify-content-center mt-3">
              <Pager
                activePage={currentPage}
                itemsPerPage={ITEMS_PER_PAGE}
                numberOfItems={pagerTotalItems}
                onActivePageChanged={handlePageChanged}
              />
            </div>

            {isLoading && data.length > 0 && !isFilterLoading && (
              <div className="text-center mt-2 text-muted">
                <Spinner animation="border" size="sm" className="me-2" />
                Laster...
              </div>
            )}

            {!hasMoreData && totalCount === null && (
              <div className="text-center text-muted mt-2">Ingen flere dokumenter.</div>
            )}
          </>
        ) : !isLoading ? (
          <Alert variant="info">Ingen medlemskapsdokumenter funnet.</Alert>
        ) : null}
      </Container>
    </PageWrapper>
  );
};

export default Filinnlesninger;
